export type StaffErrorPresentation = {
  message: string;
  technicalDetails?: string;
};

const INVALID_ERROR_TEXT = "[object Object]";
const PARENT_PORTAL_SETUP_MESSAGE =
  "The parent portal link is not set up correctly. Ask an administrator to check the school portal URL before sending.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const readText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed && trimmed !== INVALID_ERROR_TEXT ? trimmed : null;
};

const readErrorList = (value: unknown): string | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  const messages = value
    .map((entry) => (isRecord(entry) ? readText(entry.message) : readText(entry)))
    .filter((entry): entry is string => Boolean(entry));
  return messages.length ? messages.join("; ") : null;
};

const readErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    return readText(error.message);
  }
  const directText = readText(error);
  if (directText) {
    return directText;
  }
  if (!isRecord(error)) {
    return null;
  }

  const directMessage = readText(error.message);
  if (directMessage) {
    return directMessage;
  }

  const nestedErrorText = readText(error.error);
  if (nestedErrorText) {
    return nestedErrorText;
  }

  const nestedErrorMessage = isRecord(error.error) ? readErrorMessage(error.error) : null;
  if (nestedErrorMessage) {
    return nestedErrorMessage;
  }

  const dataMessage = isRecord(error.data) ? readErrorMessage(error.data) : null;
  if (dataMessage) {
    return dataMessage;
  }

  return readErrorList(error.errors);
};

export const resolveFinancialAidStaffErrorMessage = (
  error: unknown,
  fallback: string,
): string => readErrorMessage(error) ?? fallback;

export const isParentPortalSetupErrorMessage = (message: string): boolean =>
  /parent portal link|school portal url|public app url|absolute http\(s\) public app url|must be an absolute http\(s\) url with a host|public hosted origin for parent-facing email links|financial aid parent public app url|financial aid invite public app url/i.test(
    message,
  );

export const toFinancialAidStaffError = (
  error: unknown,
  fallback: string,
): StaffErrorPresentation => {
  const message = resolveFinancialAidStaffErrorMessage(error, fallback);
  if (isParentPortalSetupErrorMessage(message)) {
    return {
      message: PARENT_PORTAL_SETUP_MESSAGE,
      technicalDetails: message === PARENT_PORTAL_SETUP_MESSAGE ? undefined : message,
    };
  }
  return {
    message: fallback,
    technicalDetails: message === fallback ? undefined : message,
  };
};
